import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import AppShell from '@/components/layout/AppShell';
import PublicShell from '@/components/layout/PublicShell';
import AuthShell from '@/components/layout/AuthShell';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import StaffRoute from '@/components/auth/StaffRoute';
import CapabilityRoute from '@/components/auth/CapabilityRoute';
import OnboardingGate from '@/components/onboarding/OnboardingGate';

// Auth pages
import Login from '@/pages/auth/Login';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';

// Public pages
import HomePage from '@/pages/home/HomePage';
import PublicMapPage from '@/pages/map/PublicMapPage';
import GalleryPage from '@/pages/gallery/GalleryPage';
import ForecastPage from '@/pages/forecast/ForecastPage';
import AuthenticatedForecastPage from '@/pages/forecast/AuthenticatedForecastPage';
import InfoPage from '@/pages/info/InfoPage';

// Staff/Admin pages
import Dashboard from '@/pages/dashboard/Dashboard';
import WildfiresIndex from '@/pages/wildfires/WildfiresIndex';
import WildfireDetail from '@/pages/wildfires/WildfireDetail';
import WildfireWizard from '@/pages/wildfires/WildfireWizard';
import PermitBurnsIndex from '@/pages/permit-burns/PermitBurnsIndex';
import ContentModeration from '@/pages/images/ContentModeration';
import AdminHub from '@/pages/admin/AdminHub';
import CreateTenant from '@/pages/admin/CreateTenant';
import EditTenant from '@/pages/admin/EditTenant';
import TenantParcelsMap from '@/pages/admin/TenantParcelsMap';
import Profile from '@/pages/profile/Profile';
import StatsPage from '@/pages/stats/StatsPage';
import FirefighterList from '@/pages/firefighters/FirefighterList';
import FirefighterCreate from '@/pages/firefighters/FirefighterCreate';
import FirefighterEdit from '@/pages/firefighters/FirefighterEdit';

// FPA / Members pages
import Members from '@/pages/fpa/Members';
import EstateForm from '@/pages/fpa/EstateForm';
import EstateDetail from '@/pages/fpa/EstateDetail';
import FPAMemberForm from '@/pages/fpa/FPAMemberForm';
import FPAMemberDetail from '@/pages/fpa/FPAMemberDetail';
import ParcelPipeline from '@/pages/fpa/ParcelPipeline';
import FPASettingsHub from '@/pages/fpa/FPASettingsHub';
import InviteLanding from '@/pages/invites/InviteLanding';
import InvitesAdmin from '@/pages/admin/InvitesAdmin';
import WaterSourcesPage from '@/pages/water-sources/WaterSourcesPage';
import FPAGalleryPage from '@/pages/fpa/FPAGalleryPage';

// Onboarding
import CreateFirstTenant from '@/pages/onboarding/CreateFirstTenant';

import { useAuth } from '@/contexts/AuthContext';

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth routes — centered card layout, no chrome.
          /auth/register intentionally absent: Cognito is AdminCreateUserOnly. */}
      <Route element={<AuthShell />}>
        <Route
          path="/auth/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password/:token" element={<ResetPassword />} />
      </Route>

      {/* Public routes */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/map" element={<PublicMapPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        {/* /forecast is only public when the user is not authed. Once signed
            in, the protected /forecast route below takes over and renders
            inside AppShell so the sidebar / bottom nav stay visible. */}
        {!isAuthenticated && <Route path="/forecast" element={<ForecastPage />} />}
        <Route path="/info" element={<InfoPage />} />
      </Route>

      {/* Token-based invite landing — full-bleed, unauthenticated.
          The token IS the credential; users hit this from the SES
          invitation email and accept to enter the Cognito set-password
          flow. */}
      <Route path="/invite/:token" element={<InviteLanding />} />

      {/* Protected routes — AppShell + OnboardingGate.
          /onboarding/* lives outside the gate to break the redirect loop. */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/onboarding/create-tenant" element={<CreateFirstTenant />} />

        <Route element={<OnboardingGate />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/forecast" element={<AuthenticatedForecastPage />} />

          <Route path="/wildfires" element={<WildfiresIndex />} />
          <Route path="/wildfires/:id" element={<WildfireDetail />} />
          <Route path="/wildfires/new" element={<WildfireWizard />} />
          <Route path="/wildfires/:id/edit" element={<WildfireWizard />} />

          {/* Permit burns — placeholder section (under construction). */}
          <Route
            path="/permit-burns"
            element={<CapabilityRoute resource="wildfire" action="list"><PermitBurnsIndex /></CapabilityRoute>}
          />

          {/* Back-compat redirects */}
          <Route path="/wildfires/create" element={<Navigate to="/wildfires/new" replace />} />
          <Route path="/wildfires/edit/:id" element={<RedirectEditWildfire />} />

          <Route
            path="/firefighters"
            element={<CapabilityRoute resource="firefighter" action="list"><FirefighterList /></CapabilityRoute>}
          />
          <Route
            path="/firefighters/new"
            element={<StaffRoute><FirefighterCreate /></StaffRoute>}
          />
          <Route
            path="/firefighters/create"
            element={<Navigate to="/firefighters/new" replace />}
          />
          <Route
            path="/firefighters/:id/edit"
            element={<StaffRoute><FirefighterEdit /></StaffRoute>}
          />
          {/* Detail route currently aliases edit — there's no dedicated
              detail view, and the list row used to navigate here. */}
          <Route
            path="/firefighters/:id"
            element={<StaffRoute><FirefighterEdit /></StaffRoute>}
          />

          <Route path="/members" element={<CapabilityRoute resource="fpa_member" action="list"><Members /></CapabilityRoute>} />
          <Route
            path="/members/estates/new"
            element={<StaffRoute><EstateForm /></StaffRoute>}
          />
          <Route
            path="/members/estates/create"
            element={<Navigate to="/members/estates/new" replace />}
          />
          <Route
            path="/members/estates/:id"
            element={<StaffRoute><EstateDetail /></StaffRoute>}
          />
          <Route
            path="/members/estates/:id/edit"
            element={<StaffRoute><EstateForm /></StaffRoute>}
          />
          <Route
            path="/members/estates/edit/:id"
            element={<RedirectEditEstate />}
          />
          <Route
            path="/members/fpa/new"
            element={<StaffRoute><FPAMemberForm /></StaffRoute>}
          />
          <Route
            path="/members/fpa/create"
            element={<Navigate to="/members/fpa/new" replace />}
          />
          <Route
            path="/members/fpa/:id"
            element={<CapabilityRoute resource="fpa_member" action="read"><FPAMemberDetail /></CapabilityRoute>}
          />
          <Route
            path="/members/fpa/:id/edit"
            element={<StaffRoute><FPAMemberForm /></StaffRoute>}
          />
          <Route
            path="/members/fpa/edit/:id"
            element={<RedirectEditFPA />}
          />
          <Route
            path="/members/parcels"
            element={<StaffRoute><ParcelPipeline /></StaffRoute>}
          />

          <Route
            path="/water-sources"
            element={<CapabilityRoute resource="water_source" action="list"><WaterSourcesPage /></CapabilityRoute>}
          />

          <Route
            path="/fpa-gallery"
            element={<CapabilityRoute resource="image" action="list"><FPAGalleryPage /></CapabilityRoute>}
          />

          <Route path="/estates" element={<Navigate to="/members?tab=estates" replace />} />
          <Route path="/fpa-members" element={<Navigate to="/members?tab=fpa" replace />} />

          {/* Inbox consolidation — until C10 wraps moderation in /inbox, send to the
              current moderation page. */}
          <Route path="/inbox" element={<StaffRoute><ContentModeration /></StaffRoute>} />
          <Route path="/moderation" element={<Navigate to="/inbox" replace />} />

          {/* FPA Settings Hub — self-service home for an FPA admin.
              Available to any staff/admin; the page itself enforces the
              "manage your own FPA" rule via tenant resolution. */}
          <Route
            path="/fpa-settings"
            element={<StaffRoute><FPASettingsHub /></StaffRoute>}
          />

          {/* Admin invite tracking — list, resend, revoke. */}
          <Route
            path="/admin/invites"
            element={<StaffRoute adminOnly><InvitesAdmin /></StaffRoute>}
          />

          {/* Admin hub — single tabbed page replaces the legacy /admin/* sidebar items. */}
          <Route
            path="/admin"
            element={<StaffRoute adminOnly><AdminHub /></StaffRoute>}
          />
          <Route path="/admin/users" element={<Navigate to="/admin?tab=users" replace />} />
          <Route path="/admin/roles" element={<Navigate to="/admin?tab=roles" replace />} />
          <Route path="/admin/tenants" element={<Navigate to="/admin?tab=fpa-areas" replace />} />
          <Route path="/admin/suburbs" element={<Navigate to="/admin?tab=suburbs" replace />} />
          <Route path="/admin/settings" element={<Navigate to="/admin?tab=system" replace />} />
          <Route path="/admin/parcels/pipeline" element={<Navigate to="/members/parcels" replace />} />
          <Route path="/admin/parcels/import" element={<Navigate to="/members/parcels" replace />} />

          {/* Admin deep pages — full-screen forms / map editors stay separate routes */}
          <Route
            path="/admin/tenants/new"
            element={<StaffRoute adminOnly><CreateTenant /></StaffRoute>}
          />
          <Route
            path="/admin/tenants/create"
            element={<Navigate to="/admin/tenants/new" replace />}
          />
          <Route
            path="/admin/tenants/:id/edit"
            element={<StaffRoute adminOnly><EditTenant /></StaffRoute>}
          />
          <Route
            path="/admin/tenants/edit/:id"
            element={<RedirectEditTenant />}
          />
          {/* Legacy MUI boundaries page is superseded by the FPA Hub's
              Boundaries tab (live parcel-count preview, inline draw
              flow, auto-promote first boundary). Bounce admins there
              with the tenant pre-selected. */}
          <Route
            path="/admin/tenants/:id/boundaries"
            element={<RedirectBoundariesToHub />}
          />
          <Route
            path="/admin/tenants/:id/parcels-map"
            element={<StaffRoute adminOnly><TenantParcelsMap /></StaffRoute>}
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/* ----- small helpers for legacy /edit/:id → /:id/edit redirects ----- */

function paramRedirect(template: string) {
  return function PathParamRedirect() {
    const params = useParams();
    let dest = template;
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) dest = dest.replace(`:${k}`, String(v));
    });
    return <Navigate to={dest} replace />;
  };
}

const RedirectEditWildfire = paramRedirect('/wildfires/:id/edit');
const RedirectEditEstate = paramRedirect('/members/estates/:id/edit');
const RedirectEditFPA = paramRedirect('/members/fpa/:id/edit');
const RedirectEditTenant = paramRedirect('/admin/tenants/:id/edit');

/** Legacy `/admin/tenants/:id/boundaries` → new FPA Hub Boundaries tab.
 *  Super-admins use `?tenant_id=` so the hub picks the right tenant
 *  rather than the caller's own. */
function RedirectBoundariesToHub() {
  const { id } = useParams();
  if (!id) return <Navigate to="/fpa-settings?tab=boundaries" replace />;
  return (
    <Navigate
      to={`/fpa-settings?tab=boundaries&tenant_id=${encodeURIComponent(id)}`}
      replace
    />
  );
}

export default App;
